<template src="./ContactPostQualificationForm.html"></template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import FieldData from "@/services/fieldData";
import { ContactAddressType } from "@/models/cases/Contact";
import ContactFormBase from "../ContactFromBase/ContactFormBase.vue";
import { PostQualification } from "@/models/postQualification/PostQualification";
import { VForm } from "vuetify/lib/components/VForm/VForm";
import { NameDetails } from "@/models/postQualification/NameDetails";
import { AddressDetails } from "@/models/postQualification/AddressDetails";
import { CarDetails } from "@/models/postQualification/CarDetails";

export default defineComponent({
    extends: ContactFormBase,
    emits: ["submit", "update:postQualification"],
    props: {
        postQualification: {
            type: Object as PropType<PostQualification>,
            required: true
        },
        isSubmitting: { type: Boolean, default: false }
    },
    computed: {
        nameDetails: {
            get(): Partial<NameDetails> {
                return this.localPostQualification.nameDetails ?? {};
            },
            set(v: Partial<NameDetails>) {
                this.localPostQualification.nameDetails = {
                    firstName: "",
                    lastName: "",
                    ...v
                };
            }
        },
        addressDetails: {
            get(): Partial<AddressDetails> {
                return this.localPostQualification.addressDetails ?? {};
            },
            set(v: Partial<AddressDetails>) {
                this.localPostQualification.addressDetails = v;
            }
        },
        carDetails: {
            get(): Partial<CarDetails> {
                return this.localPostQualification.carDetails ?? {};
            },
            set(v: Partial<CarDetails>) {
                this.localPostQualification.carDetails = {
                    vin: "",
                    countryOfRegistrationCode: null,
                    ...v
                };
            }
        }
    },
    methods: {
        onAddressTypeInput(value: ContactAddressType | null): void {
            this.isBusinessAddress =
                value === ContactAddressType.BusinessAddress;
        },
        async submit(): Promise<void> {
            if ((await (this.$refs.form as VForm).validate()).valid)
                this.$emit("submit");
        }
    },
    data() {
        return {
            localPostQualification: {
                ...this.postQualification
            } as Partial<PostQualification>,
            isBusinessAddress:
                this.postQualification?.addressDetails?.addressType ===
                ContactAddressType.BusinessAddress,
            salutationData: new FieldData("Salutation", { required: true }),
            titleData: new FieldData("Title", { maxLength: 10 }),
            firstNameData: new FieldData("First name", {
                required: true,
                minLength: 2,
                maxLength: 100
            }),
            lastNameData: new FieldData("Last name", {
                required: true,
                minLength: 2,
                maxLength: 100
            }),
            addressTypesData: new FieldData("Address type", { required: true }),
            businessNameData: new FieldData("Business name", {
                required: true,
                maxLength: 200
            }),
            streetData: new FieldData("Street", {
                required: true,
                minLength: 3
            }),
            houseNumberData: new FieldData("House number", { maxLength: 10 }),
            zipCodeData: new FieldData("Postal code", {
                required: true,
                minLength: 3,
                maxLength: 15
            }),
            cityData: new FieldData("City", { required: true, minLength: 2 }),
            countryData: new FieldData("Country", { required: true }),
            vin: "",
            vinData: new FieldData("Vehicle Identification Number (VIN)", {
                required: true,
                length: 17
            }),
            countryOfRegistrationCode: new FieldData(
                "Country of Registration",
                { required: true }
            )
        };
    },
    watch: {
        postQualification: {
            handler(newVal: typeof this.postQualification) {
                Object.assign(this.localPostQualification, newVal);
            },
            deep: true
        },
        localPostQualification: {
            handler(local: typeof this.localPostQualification) {
                this.$emit("update:postQualification", { ...local });
            },
            deep: true
        }
    }
});
</script>

<style></style>
